import { FilterOperator } from '@tribeplatform/gql-client/types'

import {
  getFormattedJSONDate,
  POSTS_FIELDS,
  SEARCH_CONFIG,
  SMART_SEARCH_QUERY_LIMIT,
  SmartSearchEntityType,
  useInfiniteSearchMembers,
  useInfiniteSearchPosts,
  useInfiniteSearchSpaces,
} from '../index.js'
import { useSearchExternal } from './useSearchExternal.js'

type Props = {
  query: string
  date?: string
  tagsId?: string[]
  enabled?: boolean
  type?: SmartSearchEntityType
  spaceIds?: string[]
}

export const useQuerySmartSearch = ({
  query,
  date,
  tagsId,
  enabled = true,
  type,
  spaceIds,
}: Props) => {
  const postsQuery = useInfiniteSearchPosts({
    variables: {
      searchConfig: SEARCH_CONFIG[SmartSearchEntityType.POSTS],
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              // exclude replies
              {
                key: 'parentPostId',
                isNull: true,
              },
              ...(date
                ? [
                    {
                      key: 'publishedAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
              ...(tagsId?.length
                ? [
                    {
                      key: 'tagIds',
                      any: tagsId.map(tagId => JSON.stringify(tagId)),
                    },
                  ]
                : []),
              ...(spaceIds?.length
                ? [
                    {
                      key: 'spaceId',
                      any: spaceIds.map(spaceId => JSON.stringify(spaceId)),
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: POSTS_FIELDS,
    useInfiniteQueryOptions: {
      enabled:
        !!query.length &&
        enabled &&
        (!type || type === SmartSearchEntityType.POSTS),
    },
  })

  const membersQuery = useInfiniteSearchMembers({
    variables: {
      searchConfig: SEARCH_CONFIG[SmartSearchEntityType.MEMBERS],
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(date
                ? [
                    {
                      key: 'joinedAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: {
      nodes: {
        profilePicture: 'all',
        role: 'all',
      },
      pageInfo: 'all',
    },
    useInfiniteQueryOptions: {
      enabled:
        !!query.length &&
        enabled &&
        (!type || type === SmartSearchEntityType.MEMBERS),
    },
  })

  const spacesQuery = useInfiniteSearchSpaces({
    variables: {
      searchConfig: SEARCH_CONFIG[SmartSearchEntityType.SPACES],
      query,
      limit: SMART_SEARCH_QUERY_LIMIT,
      filters: {
        operator: FilterOperator.Or,
        groupFilters: [
          {
            matchFilters: [
              ...(date
                ? [
                    {
                      key: 'createdAt',
                      range: { gte: getFormattedJSONDate(date) },
                    },
                  ]
                : []),
            ],
            operator: FilterOperator.And,
          },
        ],
      },
    },
    fields: {
      nodes: {
        address: 'all',
        image: {
          onImage: 'all',
          onGlyph: 'all',
        },
      },
      pageInfo: 'all',
    },
    useInfiniteQueryOptions: {
      enabled:
        !!query.length &&
        enabled &&
        (!type || type === SmartSearchEntityType.SPACES),
    },
  })

  const externalsSearchQuery = useSearchExternal({
    variables: {
      query,
    },
    fields: 'all',
    useQueryOptions: {
      enabled:
        !!query.length &&
        enabled &&
        (!type || type === SmartSearchEntityType.EXTERNALS),
    },
  })

  const meta = {
    // has posts
    hasPosts:
      !postsQuery.isLoading &&
      !postsQuery.isError &&
      (postsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasMembers:
      !membersQuery.isLoading &&
      !membersQuery.isError &&
      (membersQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasSpaces:
      !spacesQuery.isLoading &&
      !spacesQuery.isError &&
      (spacesQuery.data?.pages[0]?.nodes?.length ?? 0) > 0,
    hasExternal:
      !externalsSearchQuery.isLoading &&
      !externalsSearchQuery.isError &&
      (externalsSearchQuery.data?.length ?? 0) > 0,
  }

  // a method to check if there are results in a specific type
  // if no type is provided, return true if any of the queries has results and none of them is loading
  // if a type is provided, return true if the query has results for that type and the query is not loading
  // returns undefined if the query(s) is loading
  const hasResultsByType = (
    type: SmartSearchEntityType,
  ): boolean | undefined => {
    switch (type) {
      case SmartSearchEntityType.POSTS:
        return postsQuery.isLoading || postsQuery.isError
          ? undefined
          : meta.hasPosts
      case SmartSearchEntityType.MEMBERS:
        return membersQuery.isLoading || membersQuery.isError
          ? undefined
          : meta.hasMembers
      case SmartSearchEntityType.SPACES:
        return spacesQuery.isLoading || spacesQuery.isError
          ? undefined
          : meta.hasSpaces
      case SmartSearchEntityType.EXTERNALS:
        return externalsSearchQuery.isLoading || externalsSearchQuery.isError
          ? undefined
          : !!meta.hasExternal
      default:
        // if one of the queries is loading, return undefined
        if (
          postsQuery.isLoading ||
          membersQuery.isLoading ||
          spacesQuery.isLoading ||
          externalsSearchQuery.isLoading ||
          postsQuery.isError ||
          membersQuery.isError ||
          spacesQuery.isError ||
          externalsSearchQuery.isError
        ) {
          return undefined
        }

        // if one of the queries has results, return true
        return (
          (postsQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (membersQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (spacesQuery.data?.pages[0]?.nodes?.length ?? 0) > 0 ||
          (externalsSearchQuery.data?.length ?? 0) > 0
        )
    }
  }

  return {
    postsQuery,
    membersQuery,
    spacesQuery,
    externalsSearchQuery,
    meta,
    hasResultsByType,
  }
}
